/**
 * Campos extra configurables — piezas compartidas de captura y lectura.
 *
 * La definición (secciones y campos) la administra Configuración → Campos
 * Adicionales y la sirve /api/formularioextra_obtener. Estas piezas la
 * consumen los tres puntos de captura (solicitud de crédito, venta a crédito
 * y venta al contado) y los diálogos de detalle:
 *
 *   - useFormularioExtra(): hook que carga la definición una vez ([] si el
 *     tenant no ha configurado nada o el endpoint falla).
 *   - CamposExtraForm: pinta las secciones con inputs según el tipo de cada
 *     campo. Controlado: `valores` es un objeto indexado por id de campo y
 *     onChange(idCampo, valor) reporta cada cambio.
 *   - validarCamposExtra(): valida los requeridos antes de guardar.
 *   - CamposExtraDetalle: vista de solo lectura para los diálogos de detalle
 *     (mismo estilo de etiqueta/valor que usa el detalle de solicitud).
 *
 * Los valores viajan y se guardan indexados por el id del campo (nunca por el
 * nombre): renombrar un campo en la configuración no pierde datos guardados.
 *
 * Co-deudor: las secciones con `codeudor: true` describen a la persona y se
 * capturan también para el co-deudor, con sus propios valores
 * (`datosextracodeudor`). seccionesCodeudor() las filtra y
 * usePersonaExtra() trae lo último capturado para una cédula (como deudor o
 * co-deudor) para precargar a quien vuelva.
 */
import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import InputAdornment from "@mui/material/InputAdornment";
import DynamicFormOutlinedIcon from "@mui/icons-material/DynamicFormOutlined";
import { FP, fpInputSx } from "components/financepro";
import { useApi } from "lib/api";

// Montos con coma de miles mientras se escribe (mismo criterio que los
// montos de la solicitud; el valor se guarda tal cual se muestra).
const formatMonto = (v) => {
  let s = String(v ?? "").replace(/[^\d.]/g, "");
  const punto = s.indexOf(".");
  if (punto !== -1) s = s.slice(0, punto + 1) + s.slice(punto + 1).replace(/\./g, "");
  const [ent, dec] = s.split(".");
  const entFmt = ent.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return dec !== undefined ? `${entFmt}.${dec}` : entFmt;
};

export function useFormularioExtra() {
  const { post } = useApi();
  const [secciones, setSecciones] = useState([]);

  useEffect(() => {
    let vivo = true;
    (async () => {
      try {
        const { ok, data } = await post("/api/formularioextra_obtener", {});
        if (vivo && ok) setSecciones(data.formulario?.secciones || []);
      } catch (e) {
        // Sin configuración o backend viejo: simplemente no hay campos extra.
      }
    })();
    return () => {
      vivo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return secciones;
}

// Secciones que se capturan también para el co-deudor.
export const seccionesCodeudor = (secciones) => (secciones || []).filter((s) => s.codeudor);

// Documento listo para consultar la ficha: sin separadores y con al menos
// 9 caracteres (cédula 11, RNC 9; un pasaporte corto no se precarga).
const documentoConsultable = (v) => String(v || "").replace(/[-\s.]/g, "").length >= 9;

// Completa con la ficha solo los campos que siguen vacíos: lo ya digitado
// (o cargado de la solicitud) nunca se pisa.
export function completarVacios(actual, ficha) {
  const salida = { ...(actual || {}) };
  Object.entries(ficha || {}).forEach(([k, v]) => {
    const a = salida[k];
    if (a === null || a === undefined || String(a).trim() === "") salida[k] = v;
  });
  return salida;
}

/**
 * Devuelve cargar(cedula, { cliente }) → valores capturados antes para ese
 * documento ({} si no hay o falla). Con cliente=true (el deudor de la
 * captura) trae todas las secciones de sus solicitudes y ventas anteriores;
 * sin él (fiador/co-deudor), solo las secciones de persona. No pisa nada por
 * sí mismo: el llamador mezcla con completarVacios(actual, ficha).
 */
export function usePersonaExtra() {
  const { post } = useApi();
  return async (cedula, { cliente = false } = {}) => {
    if (!documentoConsultable(cedula)) return {};
    try {
      const { ok, data } = await post("/api/formularioextra_persona", {
        cedula,
        ...(cliente ? { alcance: "cliente" } : {}),
      });
      return ok && data.datos && typeof data.datos === "object" ? data.datos : {};
    } catch (e) {
      return {};
    }
  };
}

// Valida los requeridos. Devuelve el primer mensaje de error o null.
// `quien` se agrega al mensaje para distinguir los del co-deudor.
export function validarCamposExtra(secciones, valores, quien = "") {
  for (const s of secciones || []) {
    for (const c of s.campos || []) {
      if (!c.requerido) continue;
      const v = (valores || {})[String(c.id)];
      if (v === null || v === undefined || String(v).trim() === "") {
        return `El campo "${c.nombre}" (${s.nombre}${quien ? ` — ${quien}` : ""}) es requerido`;
      }
    }
  }
  return null;
}

function CampoInput({ campo, valor, onChange }) {
  const etiqueta = campo.nombre + (campo.requerido ? " *" : "");
  const v = valor === null || valor === undefined ? "" : String(valor);

  if (campo.tipo === "opciones") {
    return (
      <TextField select fullWidth label={etiqueta} value={v} onChange={(e) => onChange(e.target.value)} sx={fpInputSx}>
        <MenuItem value="">—</MenuItem>
        {(campo.opciones || []).map((o) => (
          <MenuItem key={o} value={o}>
            {o}
          </MenuItem>
        ))}
      </TextField>
    );
  }
  if (campo.tipo === "sino") {
    return (
      <TextField select fullWidth label={etiqueta} value={v} onChange={(e) => onChange(e.target.value)} sx={fpInputSx}>
        <MenuItem value="">—</MenuItem>
        <MenuItem value="S">Sí</MenuItem>
        <MenuItem value="N">No</MenuItem>
      </TextField>
    );
  }
  if (campo.tipo === "fecha") {
    return (
      <TextField
        fullWidth
        type="date"
        label={etiqueta}
        InputLabelProps={{ shrink: true }}
        value={v}
        onChange={(e) => onChange(e.target.value)}
        sx={fpInputSx}
      />
    );
  }
  if (campo.tipo === "monto") {
    return (
      <TextField
        fullWidth
        label={etiqueta}
        value={v}
        onChange={(e) => onChange(formatMonto(e.target.value))}
        InputProps={{ startAdornment: <InputAdornment position="start">RD$</InputAdornment> }}
        sx={fpInputSx}
      />
    );
  }
  if (campo.tipo === "numero") {
    return (
      <TextField fullWidth type="number" label={etiqueta} value={v} onChange={(e) => onChange(e.target.value)} sx={fpInputSx} />
    );
  }
  // texto y telefono
  return (
    <TextField
      fullWidth
      label={etiqueta}
      value={v}
      onChange={(e) => onChange(e.target.value)}
      inputProps={campo.tipo === "telefono" ? { inputMode: "tel" } : undefined}
      sx={fpInputSx}
    />
  );
}

/**
 * Secciones de captura. `sectionSx` permite heredar el estilo de tarjeta del
 * diálogo anfitrión (el sectionBox de cada pantalla); sin él usa uno propio
 * equivalente.
 */
export function CamposExtraForm({ secciones, valores, onChange, sectionSx, sufijo = "" }) {
  const lista = (secciones || []).filter((s) => (s.campos || []).length);
  if (!lista.length) return null;

  const estilo =
    sectionSx || {
      border: `1px solid ${FP.color.border}`,
      borderRadius: 2.5,
      p: { xs: 2, sm: 2.6 },
      mb: 2.5,
      bgcolor: FP.color.cardBg,
      boxShadow: FP.shadow.soft,
    };

  return (
    <>
      {lista.map((seccion) => (
        <Box key={seccion.id} sx={estilo}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, mb: 2 }}>
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: 1.4,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: `${FP.color.navy}14`,
                color: FP.color.navy,
              }}
            >
              <DynamicFormOutlinedIcon sx={{ fontSize: 19 }} />
            </Box>
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: "0.74rem",
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                color: FP.color.textSecondary,
              }}
            >
              {seccion.nombre}
              {sufijo ? ` — ${sufijo}` : ""}
            </Typography>
          </Box>
          <Grid container spacing={2}>
            {seccion.campos.map((campo) => (
              <Grid item xs={12} sm={4} key={campo.id}>
                <CampoInput
                  campo={campo}
                  valor={(valores || {})[String(campo.id)]}
                  onChange={(v) => onChange(String(campo.id), v)}
                />
              </Grid>
            ))}
          </Grid>
        </Box>
      ))}
    </>
  );
}

/** Vista de solo lectura para diálogos de detalle. */
export function CamposExtraDetalle({ secciones, valores, sufijo = "" }) {
  const vals = valores || {};
  const lista = (secciones || [])
    .map((s) => ({
      ...s,
      campos: (s.campos || []).filter((c) => {
        const v = vals[String(c.id)];
        return v !== null && v !== undefined && String(v).trim() !== "";
      }),
    }))
    .filter((s) => s.campos.length);
  if (!lista.length) return null;

  const mostrar = (campo, v) => {
    if (campo.tipo === "sino") return v === "S" ? "Sí" : v === "N" ? "No" : String(v);
    if (campo.tipo === "monto") return `RD$ ${v}`;
    return String(v);
  };

  return (
    <>
      {lista.map((seccion) => (
        <Box key={seccion.id} sx={{ mb: 2.5 }}>
          <Typography
            sx={{
              fontWeight: 800,
              fontSize: "0.74rem",
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              color: FP.color.textSecondary,
              mb: 1,
            }}
          >
            {seccion.nombre}
            {sufijo ? ` — ${sufijo}` : ""}
          </Typography>
          <Grid container spacing={1.5}>
            {seccion.campos.map((campo) => (
              <Grid item xs={6} sm={4} key={campo.id}>
                <Typography
                  sx={{
                    fontSize: "0.68rem",
                    fontWeight: 800,
                    letterSpacing: "0.06em",
                    textTransform: "uppercase",
                    color: FP.color.textMuted,
                  }}
                >
                  {campo.nombre}
                </Typography>
                <Typography sx={{ fontSize: "0.88rem", color: FP.color.textPrimary }}>
                  {mostrar(campo, vals[String(campo.id)])}
                </Typography>
              </Grid>
            ))}
          </Grid>
        </Box>
      ))}
    </>
  );
}
